import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { ContextMenuItem } from './ContextMenu';

interface ActionPanelProps {
  /**
   * Items to render. Same structure as ContextMenu, so the same builder can
   * feed both surfaces. Items flagged `primary` become a featured card at the
   * top; `separator` items delimit groups and may carry a `sectionTitle` for
   * the group that follows; items with `danger: true` render red.
   */
  items: ContextMenuItem[];
}

interface Group {
  title?: string;
  items: ContextMenuItem[];
}

/**
 * Click-only counterpart to ContextMenu. Renders the action list as iOS
 * Settings-style grouped cards so every action is reachable on touch devices
 * with large tap targets and clear visual grouping.
 *
 *   Primary card (if any) → filled/tinted featured toggle
 *   Each group → bordered card with hairline-separated rows, optional label
 *   Danger-only groups automatically get red styling
 *
 * Keep the panel in sync with the right-click context menu by feeding both
 * from the same `build*Actions()` helper.
 */
export function ActionPanel({ items }: ActionPanelProps) {
  const primary = items.find((i) => i.primary && !i.separator);

  // Walk items (skipping the primary) and split into groups. A separator item
  // starts a new group; its `sectionTitle` becomes that group's label.
  const groups: Group[] = [];
  let current: Group = { items: [] };

  for (const item of items) {
    if (item === primary) continue;
    if (item.separator) {
      if (current.items.length > 0) groups.push(current);
      current = { title: item.sectionTitle, items: [] };
      continue;
    }
    current.items.push(item);
  }
  if (current.items.length > 0) groups.push(current);

  return (
    <div className="mb-6">
      <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
        Actions
      </h3>

      {primary && <PrimaryCard item={primary} />}

      {groups.map((group, i) => (
        <GroupCard key={i} group={group} />
      ))}
    </div>
  );
}

function PrimaryCard({ item }: { item: ContextMenuItem }) {
  const Icon = item.icon;
  const isLoader = Icon?.displayName === 'Loader2';
  return (
    <button
      type="button"
      onClick={item.onClick}
      disabled={item.disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-4 py-3 mb-4 rounded-lg text-left',
        'bg-accent-blue/10 border border-accent-blue/30 text-text-primary',
        'hover:bg-accent-blue/15 hover:border-accent-blue/50',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        'transition-colors',
      )}
    >
      {Icon && (
        <Icon
          size={20}
          className={clsx(
            'text-accent-blue flex-shrink-0',
            isLoader && 'animate-spin',
          )}
        />
      )}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium">{item.label}</span>
        {item.sublabel && (
          <span className="text-xs text-text-muted truncate">{item.sublabel}</span>
        )}
      </div>
      <ChevronRight size={16} className="text-accent-blue/60 flex-shrink-0" />
    </button>
  );
}

function GroupCard({ group }: { group: Group }) {
  const isDangerGroup = group.items.every((i) => i.danger);

  return (
    <div className="mb-4 last:mb-0">
      {group.title && (
        <h4
          className={clsx(
            'text-2xs font-semibold uppercase tracking-widest mb-2 px-1',
            isDangerGroup ? 'text-accent-red/80' : 'text-text-muted',
          )}
        >
          {group.title}
        </h4>
      )}
      <div
        className={clsx(
          'rounded-lg border overflow-hidden divide-y divide-subtle',
          isDangerGroup
            ? 'bg-accent-red/5 border-accent-red/20'
            : 'bg-dbgo-panel',
        )}
      >
        {group.items.map((item, i) => (
          <MenuRow key={i} item={item} />
        ))}
      </div>
    </div>
  );
}

function MenuRow({ item }: { item: ContextMenuItem }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={item.onClick}
      disabled={item.disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
        item.danger
          ? 'text-accent-red hover:bg-accent-red/10'
          : 'text-text-primary hover:bg-dbgo-tab-hover',
        'disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed',
      )}
    >
      {Icon && (
        <Icon
          size={16}
          className={clsx(
            'flex-shrink-0',
            item.danger ? 'text-accent-red' : 'text-text-secondary',
          )}
        />
      )}
      <span className="text-sm flex-1">{item.label}</span>
      {!item.disabled && !item.danger && (
        <ChevronRight size={14} className="text-text-muted flex-shrink-0" />
      )}
    </button>
  );
}
